import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { type TransferSearchTransfersData, type UnifiedTransfer } from '@column/api';
import { Icon, Tooltip } from '@column/column-ui-kit';
import {
  Badge,
  type DataTableColumnDef,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
  Timestamp,
  cn,
} from '@column/ui';

import { PopoverFilterAmountData, PopoverFilterTransferDateData, PopoverFilterTransferTypeData } from '~/components';
import { useDebounce } from '~/hooks/useDebounce';
import { useQueryParams } from '~/hooks/useQueryParams';
import { TransferFilterType } from '~/hooks/useTransfers';
import { usePaginationState } from '~/lib/api/hooks/usePaginationState';
import { TransferEntryType, convertTransferAmount } from '~/repositories';
import { TableTimeFormat } from '~/repositories/UserRepository';
import { formatDateTime, getFormattedDate } from '~/util';

import { BatchStatusBadge } from './components/BatchStatusBadge';
import { TransferAmount } from './components/TransferAmount';
import { TransferStatusBadge } from './components/TransferStatusBadge';
import { TransferTypeBadge } from './components/TransferTypeBadge';
import { TransferDirectionBadge } from './direction';
import { getBatchNetAmount, isBatchAmountUnavailable, isBatchTransfer } from './utils';

// Maps the camelCase filter keys we use throughout the dashboard to the
// snake_case `query` parameters expected by @column/api's transfer search.
const FILTER_KEY_TO_API: Partial<Record<keyof TransferFilterType, string>> = {
  bankAccountId: 'bank_account_id',
  senderBankAccountId: 'sender_bank_account_id',
  receiverBankAccountId: 'receiver_bank_account_id',
  accountNumberId: 'account_number_id',
  senderAccountNumberId: 'sender_account_number_id',
  receiverAccountNumberId: 'receiver_account_number_id',
  counterpartyId: 'counterparty_id',
  transferBatchId: 'transfer_batch_id',
  transferId: 'transfer_id',
  isIncoming: 'is_incoming',
  amountGt: 'amount_gt',
  amountGte: 'amount_gte',
  amountLt: 'amount_lt',
  amountLte: 'amount_lte',
  createdAtGt: 'created_at_gt',
  createdAtGte: 'created_at_gte',
  createdAtLt: 'created_at_lt',
  createdAtLte: 'created_at_lte',
};

export const toTransferApiQuery = (filters: TransferFilterType): NonNullable<TransferSearchTransfersData['query']> => {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(filters)) {
    if (value === undefined || value === null || value === '') continue;
    if (key === 'page') continue;
    out[FILTER_KEY_TO_API[key as keyof TransferFilterType] ?? key] = value;
  }
  return out as NonNullable<TransferSearchTransfersData['query']>;
};

const FOLDER_CHIP_CLASS_NAME =
  'h-5 shrink-0 gap-1 rounded-md border-border bg-muted py-0.5 text-xs leading-[14px] text-muted-foreground [&>svg]:size-4 [&>svg]:[--icon-color:currentColor] [&>svg]:[--icon-size-x:16px] [&>svg]:[--icon-size-y:16px] [&>svg]:[--icon-size:16px]';

interface TransferTableColumnsOptions {
  bankAccountId?: string;
  timeFormat: TableTimeFormat;
  onTimeFormatChange: (value: TableTimeFormat) => void;
  showBatchMembership?: boolean;
  batchStatus?: string;
}

export const transferTableColumns = ({
  bankAccountId,
  timeFormat,
  onTimeFormatChange,
  showBatchMembership = true,
  batchStatus,
}: TransferTableColumnsOptions): DataTableColumnDef<UnifiedTransfer>[] => [
  {
    header: 'Amount / Description',
    accessor: 'description',
    width: 'minmax(300px, auto)',
    className: 'min-w-0',
    cell: ({ row }) => {
      const type = row.type as TransferEntryType;
      const convertAmount = convertTransferAmount(type, Boolean(row.is_incoming));
      let description = row.description ?? '';
      const checkNumber = row.auxiliary_on_us ?? row.check_number;
      if ((row.type === 'check_debit' || row.type === 'check_credit') && checkNumber) {
        description = `#${checkNumber}${description ? ` ${description}` : ''}`;
      }
      const isBatch = row.type === 'ach_batch' || row.type === 'check_batch';
      const batchCount = batchStatus ? (row.batch_status_counts?.[batchStatus] ?? 0) : (row.batch_transfer_count ?? 0);
      const batchAmount = batchStatus ? (row.batch_status_amounts?.[batchStatus] ?? 0) : (row.amount ?? 0);
      const amountUnavailable = isBatchAmountUnavailable(row);
      const isAdding = isBatch
        ? amountUnavailable || batchAmount === 0
          ? undefined
          : getBatchNetAmount({ type: row.type, amount: batchAmount }) > 0
        : row.type === 'book'
          ? bankAccountId
            ? row.sender_internal_account?.bank_account_id !== bankAccountId
            : undefined
          : convertAmount !== -1;
      return (
        <TransferAmount
          amount={amountUnavailable ? null : isBatch ? Math.abs(batchAmount) : (row.amount ?? 0)}
          currencyCode={row.currency_code}
          description={description}
          badge={
            isBatch ? (
              <Tooltip
                content={`This batch contains ${batchCount} ${batchCount === 1 ? 'transfer' : 'transfers'}${batchStatus ? ` with status ${batchStatus}` : ''}`}
                placement="right"
                delay={200}
                className="flex"
              >
                <Badge variant="outline" className={cn(FOLDER_CHIP_CLASS_NAME, 'pl-[3px] pr-[5px]')}>
                  <Icon.Folder />
                  {batchCount}
                </Badge>
              </Tooltip>
            ) : showBatchMembership && row.transfer_batch_id ? (
              <Tooltip content="This transfer is part of a batch" placement="right" delay={200} className="flex">
                <Badge
                  variant="outline"
                  aria-label="Part of a batch"
                  className={cn(FOLDER_CHIP_CLASS_NAME, 'px-[3px]')}
                >
                  <Icon.Folder />
                </Badge>
              </Tooltip>
            ) : undefined
          }
          isAdding={isAdding}
        />
      );
    },
  },
  {
    header: 'Type',
    accessor: 'type',
    width: 'minmax(min-content, 150px)',
    cell: ({ value }) => {
      return <TransferTypeBadge type={value as TransferEntryType} />;
    },
  },
  {
    header: 'Status',
    accessor: 'status',
    width: 'minmax(min-content, 150px)',
    cell: ({ row, value }) => {
      if (isBatchTransfer(row)) return <BatchStatusBadge row={row} />;
      const type = row.type as TransferEntryType;
      const status = row.pending_approval ? 'pre_review' : value;
      return <TransferStatusBadge status={status} type={type} />;
    },
  },
  {
    header: 'Direction',
    accessor: 'is_incoming',
    width: 'minmax(min-content, 150px)',
    cell: ({ row }) => {
      const type = row.type as TransferEntryType;
      const isIncoming = Boolean(row.is_incoming);
      return <TransferDirectionBadge type={type} isIncoming={isIncoming} />;
    },
  },
  {
    accessor: 'created_at',
    header: (
      <DropdownMenu modal={false}>
        <DropdownMenuTrigger className="inline-flex items-center gap-1 outline-none [&_svg]:[--icon-color:currentColor]">
          Created <Icon.ChevronDown />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuRadioGroup
            value={timeFormat}
            onValueChange={(value) => onTimeFormatChange(value as TableTimeFormat)}
          >
            <DropdownMenuRadioItem value={TableTimeFormat.Relative}>Relative Time</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value={TableTimeFormat.Absolute}>Date / Time</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
    width: 'min-content',
    cell: ({ value }) => {
      if (!value) return null;
      return (
        <Timestamp
          date={value}
          className="text-xs text-muted-foreground"
          formatHover={(hoverDate) =>
            formatDateTime(hoverDate, { timeZoneFormat: timeFormat === TableTimeFormat.Relative ? 'local' : 'utc' })
          }
        >
          {timeFormat === TableTimeFormat.Relative
            ? getFormattedDate(value, 'time-passed')
            : formatDateTime(value, { timeZoneFormat: 'local' })}
        </Timestamp>
      );
    },
  },
];

interface UseTransfersFiltersOptions {
  bankAccountId?: string;
  defaultPageSize: number;
}

export const useTransfersFilters = ({ bankAccountId, defaultPageSize }: UseTransfersFiltersOptions) => {
  const {
    queryParams,
    setQueryParams: setUrlQueryParams,
    getQueryParam: getUrlParam,
  } = useQueryParams<TransferFilterType>();

  const initialQuery = getUrlParam('query' as keyof TransferFilterType) ?? '';
  const [queryInput, setQueryInput] = useState<string>(() => initialQuery);
  const [debouncedQueryInput, setDebouncedQueryInput] = useState<string>(() => initialQuery);

  const hasSeededQueryFromUrlRef = useRef<boolean>(initialQuery.length > 0);
  useLayoutEffect(() => {
    if (hasSeededQueryFromUrlRef.current) return;
    hasSeededQueryFromUrlRef.current = true;
    const urlQuery = getUrlParam('query' as keyof TransferFilterType) ?? '';
    if (urlQuery.length === 0) return;
    setQueryInput(urlQuery);
    setDebouncedQueryInput(urlQuery);
  }, [getUrlParam]);

  const [debouncedApplyQuery, cancelApplyQuery] = useDebounce((value: string) => {
    setDebouncedQueryInput(value);
  }, 300);

  const handleQueryInputChange = useCallback(
    (value: string) => {
      setQueryInput(value);
      debouncedApplyQuery(value);
    },
    [debouncedApplyQuery]
  );

  const [bankAccountIdFilterData, setBankAccountIdFilterData] = useState<string | undefined>(() => {
    if (bankAccountId) return undefined;
    if (!queryParams) return undefined;
    const { bankAccountId: queryBankAccountId } = queryParams;
    return queryBankAccountId && queryBankAccountId.length > 0 ? queryBankAccountId : undefined;
  });

  const [amountFilterData, setAmountFilterData] = useState<PopoverFilterAmountData | undefined>(() => {
    if (!queryParams) return undefined;
    const { amount, amountGte, amountLte } = queryParams;
    return {
      amount: amount ? Number(amount) : undefined,
      amountGte: amountGte ? Number(amountGte) : undefined,
      amountLte: amountLte ? Number(amountLte) : undefined,
    };
  });
  const isAmountFilterActive = useMemo(
    () =>
      Boolean(
        (amountFilterData?.amount && amountFilterData.amount > 0) ||
        (amountFilterData?.amountGte && amountFilterData.amountGte > 0) ||
        (amountFilterData?.amountLte && amountFilterData.amountLte > 0)
      ),
    [amountFilterData]
  );

  const [transferTypeFilterData, setTransferTypeFilterData] = useState<PopoverFilterTransferTypeData>(() => {
    if (!queryParams) return {};
    const { isIncoming, type } = queryParams;
    return {
      isIncoming:
        typeof isIncoming !== 'undefined'
          ? typeof isIncoming === 'string'
            ? isIncoming === 'true'
            : isIncoming === true
          : undefined,
      type: type ? (type.split(',') as unknown as PopoverFilterTransferTypeData['type']) : [],
    };
  });
  const isTransferTypeFilterActive = useMemo(
    () =>
      Boolean(
        transferTypeFilterData?.type?.some((entry) => Boolean(entry)) ||
        typeof transferTypeFilterData?.isIncoming === 'boolean'
      ),
    [transferTypeFilterData]
  );

  const [transferStatusFilterData, setTransferStatusFilterData] = useState<string[] | undefined>(() => {
    if (!queryParams) return [];
    const { status } = queryParams;
    return status ? status.split(',') : [];
  });

  const [transferDateFilterData, setTransferDateFilterData] = useState<Partial<PopoverFilterTransferDateData>>(() => {
    if (!queryParams) return {};
    const { createdAtGte, createdAtLt } = queryParams;
    return {
      createdAtGte: createdAtGte && createdAtGte.length > 0 ? createdAtGte : undefined,
      createdAtLt: createdAtLt && createdAtLt.length > 0 ? createdAtLt : undefined,
    };
  });
  const isDateFilterActive = useMemo(
    () => Boolean(transferDateFilterData?.createdAtGte && transferDateFilterData?.createdAtLt),
    [transferDateFilterData]
  );

  const {
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    reset: resetPagination,
    setPageSize,
  } = usePaginationState({
    defaultPageSize,
    initialCursor: queryParams?.startingAfter,
    initialPageIndex: typeof queryParams?.pageIndex === 'number' ? queryParams.pageIndex : 0,
  });

  useEffect(() => {
    setUrlQueryParams((prev) => {
      const next: TransferFilterType = { ...(prev ?? {}) };
      next.query = debouncedQueryInput || undefined;
      next.bankAccountId = bankAccountId ? undefined : bankAccountIdFilterData || undefined;
      next.amount =
        amountFilterData?.amount && amountFilterData.amount > 0
          ? (String(amountFilterData.amount) as never)
          : undefined;
      next.amountGte =
        amountFilterData?.amountGte && amountFilterData.amountGte > 0
          ? (String(amountFilterData.amountGte) as never)
          : undefined;
      next.amountLte =
        amountFilterData?.amountLte && amountFilterData.amountLte > 0
          ? (String(amountFilterData.amountLte) as never)
          : undefined;
      next.type =
        transferTypeFilterData.type && transferTypeFilterData.type.length > 0
          ? ((transferTypeFilterData.type as unknown as string[]).join(',') as TransferFilterType['type'])
          : undefined;
      next.isIncoming =
        typeof transferTypeFilterData.isIncoming === 'boolean' ? transferTypeFilterData.isIncoming : undefined;
      next.status =
        transferStatusFilterData && transferStatusFilterData.length > 0
          ? (transferStatusFilterData.join(',') as TransferFilterType['status'])
          : undefined;
      next.createdAtGte = transferDateFilterData?.createdAtGte || undefined;
      next.createdAtLt = transferDateFilterData?.createdAtLt || undefined;
      next.startingAfter = cursor || undefined;
      next.pageIndex = pageIndex > 0 ? pageIndex : undefined;
      return next;
    });
  }, [
    setUrlQueryParams,
    bankAccountId,
    debouncedQueryInput,
    bankAccountIdFilterData,
    amountFilterData,
    transferTypeFilterData,
    transferStatusFilterData,
    transferDateFilterData,
    cursor,
    pageIndex,
  ]);

  const filters = useMemo<TransferFilterType>(() => {
    const f: TransferFilterType = {};
    if (bankAccountId) f.bankAccountId = bankAccountId;
    if (debouncedQueryInput) f.query = debouncedQueryInput;
    if (!bankAccountId && bankAccountIdFilterData) f.bankAccountId = bankAccountIdFilterData;
    if (queryParams?.counterpartyId) f.counterpartyId = queryParams.counterpartyId;
    if (queryParams?.accountNumberId) f.accountNumberId = queryParams.accountNumberId;
    if (queryParams?.transferBatchId) f.transferBatchId = queryParams.transferBatchId;
    if (transferTypeFilterData.type && transferTypeFilterData.type.length > 0) {
      f.type = (transferTypeFilterData.type as unknown as string[]).join(',') as TransferFilterType['type'];
    }
    if (transferStatusFilterData && transferStatusFilterData.length > 0) {
      f.status = transferStatusFilterData.join(',') as TransferFilterType['status'];
    }
    if (typeof transferTypeFilterData.isIncoming === 'boolean') f.isIncoming = transferTypeFilterData.isIncoming;
    if (amountFilterData?.amount && amountFilterData.amount > 0) f.amount = String(amountFilterData.amount);
    if (amountFilterData?.amountGte && amountFilterData.amountGte > 0) f.amountGte = String(amountFilterData.amountGte);
    if (amountFilterData?.amountLte && amountFilterData.amountLte > 0) f.amountLte = String(amountFilterData.amountLte);
    if (transferDateFilterData?.createdAtGte) f.createdAtGte = transferDateFilterData.createdAtGte;
    if (transferDateFilterData?.createdAtLt) f.createdAtLt = transferDateFilterData.createdAtLt;
    return f;
  }, [
    bankAccountId,
    debouncedQueryInput,
    bankAccountIdFilterData,
    queryParams?.counterpartyId,
    queryParams?.accountNumberId,
    queryParams?.transferBatchId,
    transferTypeFilterData,
    transferStatusFilterData,
    amountFilterData,
    transferDateFilterData,
  ]);

  const isFilterActive = useMemo(
    () =>
      Boolean(
        queryInput ||
        (!bankAccountId && bankAccountIdFilterData) ||
        queryParams?.counterpartyId ||
        queryParams?.accountNumberId ||
        isAmountFilterActive ||
        isTransferTypeFilterActive ||
        (transferStatusFilterData && transferStatusFilterData.length > 0) ||
        isDateFilterActive
      ),
    [
      bankAccountId,
      queryInput,
      bankAccountIdFilterData,
      queryParams?.counterpartyId,
      queryParams?.accountNumberId,
      isAmountFilterActive,
      isTransferTypeFilterActive,
      transferStatusFilterData,
      isDateFilterActive,
    ]
  );

  const resetAllFilters = useCallback(() => {
    cancelApplyQuery();
    setBankAccountIdFilterData(undefined);
    setAmountFilterData({ amount: undefined, amountGte: undefined, amountLte: undefined });
    setTransferTypeFilterData({});
    setTransferStatusFilterData([]);
    setTransferDateFilterData({});
    setQueryInput('');
    setDebouncedQueryInput('');
    setUrlQueryParams(() => undefined);
    resetPagination();
  }, [cancelApplyQuery, resetPagination, setUrlQueryParams]);

  return {
    queryInput,
    setQueryInput,
    debouncedQueryInput,
    setDebouncedQueryInput,
    handleQueryInputChange,
    cancelApplyQuery,
    bankAccountIdFilterData,
    setBankAccountIdFilterData,
    amountFilterData,
    setAmountFilterData,
    isAmountFilterActive,
    transferTypeFilterData,
    setTransferTypeFilterData,
    isTransferTypeFilterActive,
    transferStatusFilterData,
    setTransferStatusFilterData,
    transferDateFilterData,
    setTransferDateFilterData,
    isDateFilterActive,
    cursor,
    hasPrevious,
    onNext,
    onPrevious,
    pageIndex,
    resetPagination,
    setPageSize,
    filters,
    isFilterActive,
    resetAllFilters,
    queryParams,
    setUrlQueryParams,
  };
};
